import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { notificationsApi, type AppNotification } from "@/lib/api/notifications";
import { useToast } from "@/contexts/ToastContext";

export default function NotificationsPage() {
  const toast = useToast();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await notificationsApi.listMine();
      setItems(data);
    } catch {
      toast.error("Could not load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markRead = async (id: string) => {
    try {
      await notificationsApi.markRead(id);
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n))
      );
    } catch {
      toast.error("Could not update notification");
    }
  };

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Notifications"
          description="Updates and alerts for your account"
          breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Notifications" }]}
          showSearch={false}
          titleSize="md"
        />

        {loading ? (
          <LoadingSpinner />
        ) : items.length === 0 ? (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="py-10 text-center text-slate-500">
              No notifications yet.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {items.map((n) => (
              <Card
                key={n.id}
                className={`border shadow-sm ${
                  n.readAt ? "border-slate-200 bg-white" : "border-primary/30 bg-primary/[0.03]"
                }`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold">
                    {n.subject || "Notification"}
                  </CardTitle>
                  <p className="text-xs text-slate-400">{new Date(n.createdAt).toLocaleString()}</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.content}</p>
                  {!n.readAt && (
                    <Button size="sm" variant="outline" onClick={() => markRead(n.id)}>
                      Mark as read
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </PageContainer>
    </AppLayout>
  );
}
